import type { TEmail, TPhone } from "@/api/shared/contacts.ts";
import type { ESubjects } from "../../auth/ability";
import type { TGroup } from "../groups";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { TPracticeStaffUserSettings } from "../practiceStaffSettings";
import type { TPracticeStaffAPIKeyResponse } from "@/api/models/practice/apiKeys";
import { EducationEnum as EEducation } from "@/api/generated/practice.types";
import type { TLinkedPracticeLink } from "../practice/types";
import type { TAddress } from "@/api/shared/address";
import type { TFile } from "@/api/shared/file";

export { EEducation };

export enum ESex {
  MALE = "MA",
  FEMALE = "FE",
}

export type TPracticeStaffBadge = {
  code: string;
  color: string;
  priority: number;
  protected: boolean;
};

/** Display-only subset of a practice, inlined under {@link TPracticeStaffCompanyData}. */
export type TPracticeStaffPracticeData = {
  id: string;
  name: string;
  picture?: TFile | null;
  phones?: TPhone[];
  emails?: TEmail[];
  addresses?: TAddress[];
};

/** Display-only subset of a company, inlined via {@link IPracticeStaffFilter.include_companies_data}. */
export type TPracticeStaffCompanyData = {
  id: string;
  name: string;
  picture?: TFile | null;
  phones?: TPhone[];
  addresses?: TAddress[];
  practices_data?: TPracticeStaffPracticeData[];
};

export interface TPracticeStaff {
  type: EPracticeStaffType;
  groups: TGroup[];
  id: string;

  emails?: TEmail[];
  phones?: TPhone[];
  badges?: TPracticeStaffBadge[];

  linked_practices?: TLinkedPracticeLink[];
  companies?: {
    id: string;
    practices: string[];
  }[];
  /** Only present when the request passes `include_companies_data`. */
  companies_data?: TPracticeStaffCompanyData[];

  first_name: string;
  last_name: string;
  picture?: { id: string; file: string };
  picture_default: string;

  status?: EStatus;
  company?: string;
  middle_name?: string;
  sex?: ESex;
  created_at: string;

  preferred_communication_channel?: EPreferredCommunicationChannel;
  place_of_education?: string;
  licences?: TLicense[];
  education: EEducation[];

  taxonomy_code: ETaxonomyCode;
  apiKeys?: TPracticeStaffAPIKeyResponse[];

  user_settings: TPracticeStaffUserSettings;

  permissions?: TPermission[];

  external_calendar_url?: string;
}

export const EEducationLabelMap: Record<EEducation, string> = {
  [EEducation.DDS]: "DDS",
  [EEducation.DMD]: "DMD",
  [EEducation.DDS_PHD]: "DDS, PhD",
  [EEducation.DMD_PHD]: "DMD, PhD",
  [EEducation.MS]: "MS",
  [EEducation.MSD]: "MSD",
};

export type TLicense = {
  licence_number: string;
  licence_state: ELicenseState;
  licence_expiration_date: string;
};

export type TPermission = {
  subject: ESubjects;
  action: string;
};

export enum EPracticeStaffType {
  ADMIN = "admin",
  PRACTICE_ASSISTANT = "practice_assistant",
  TREATMENT_COORDINATOR = "treatment_coordinator",
  HYGIENIST = "hygienist",
  GP = "gp",
  ORTHODONTIST = "orthodontist",
  BUSINESS_SUPPORT = "business_support",
  GP_PRACTICE_ASSISTANT = "gp_practice_assistant",
  ORTHO_PRACTICE_ASSISTANT = "ortho_practice_assistant",
  GP_OFFICE_MANAGER = "gp_office_manager",
  ORTHO_OFFICE_MANAGER = "ortho_office_manager",
  ACCOUNTING_FINANCE = "accounting_finance",
}

export enum EPracticeStaffTypeShort {
  PRACTICE_ASSISTANT = "PA",
  TREATMENT_COORDINATOR = "TC",
  HYGIENIST = "HY",
  GP = "GP",
  ORTHODONTIST = "ORT",
  BUSINESS_SUPPORT = "BS",
  GP_PRACTICE_ASSISTANT = "GPPA",
  ORTHO_PRACTICE_ASSISTANT = "OPA",
  GP_OFFICE_MANAGER = "GPOM",
  ORTHO_OFFICE_MANAGER = "OOM",
  ACCOUNTING_FINANCE = "AC",
  ADMIN = "ADMIN",
}

export const StaffTypeShortMap: Record<EPracticeStaffType, EPracticeStaffTypeShort> = {
  [EPracticeStaffType.PRACTICE_ASSISTANT]: EPracticeStaffTypeShort.PRACTICE_ASSISTANT,
  [EPracticeStaffType.TREATMENT_COORDINATOR]: EPracticeStaffTypeShort.TREATMENT_COORDINATOR,
  [EPracticeStaffType.HYGIENIST]: EPracticeStaffTypeShort.HYGIENIST,
  [EPracticeStaffType.GP]: EPracticeStaffTypeShort.GP,
  [EPracticeStaffType.ORTHODONTIST]: EPracticeStaffTypeShort.ORTHODONTIST,
  [EPracticeStaffType.BUSINESS_SUPPORT]: EPracticeStaffTypeShort.BUSINESS_SUPPORT,
  [EPracticeStaffType.GP_PRACTICE_ASSISTANT]: EPracticeStaffTypeShort.GP_PRACTICE_ASSISTANT,
  [EPracticeStaffType.ORTHO_PRACTICE_ASSISTANT]: EPracticeStaffTypeShort.ORTHO_PRACTICE_ASSISTANT,
  [EPracticeStaffType.GP_OFFICE_MANAGER]: EPracticeStaffTypeShort.GP_OFFICE_MANAGER,
  [EPracticeStaffType.ORTHO_OFFICE_MANAGER]: EPracticeStaffTypeShort.ORTHO_OFFICE_MANAGER,
  [EPracticeStaffType.ACCOUNTING_FINANCE]: EPracticeStaffTypeShort.ACCOUNTING_FINANCE,
  [EPracticeStaffType.ADMIN]: EPracticeStaffTypeShort.ADMIN,
};

export enum EStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum ELicenseState {
  // US States
  ALABAMA = "alabama",
  ALASKA = "alaska",
  ARIZONA = "arizona",
  ARKANSAS = "arkansas",
  CALIFORNIA = "california",
  COLORADO = "colorado",
  CONNECTICUT = "connecticut",
  DELAWARE = "delaware",
  FLORIDA = "florida",
  GEORGIA = "georgia",
  HAWAII = "hawaii",
  IDAHO = "idaho",
  ILLINOIS = "illinois",
  INDIANA = "indiana",
  IOWA = "iowa",
  KANSAS = "kansas",
  KENTUCKY = "kentucky",
  LOUISIANA = "louisiana",
  MAINE = "maine",
  MARYLAND = "maryland",
  MASSACHUSETTS = "massachusetts",
  MICHIGAN = "michigan",
  MINNESOTA = "minnesota",
  MISSISSIPPI = "mississippi",
  MISSOURI = "missouri",
  MONTANA = "montana",
  NEBRASKA = "nebraska",
  NEVADA = "nevada",
  NEW_HAMPSHIRE = "new_hampshire",
  NEW_JERSEY = "new_jersey",
  NEW_MEXICO = "new_mexico",
  NEW_YORK = "new_york",
  NORTH_CAROLINA = "north_carolina",
  NORTH_DAKOTA = "north_dakota",
  OHIO = "ohio",
  OKLAHOMA = "oklahoma",
  OREGON = "oregon",
  PENNSYLVANIA = "pennsylvania",
  RHODE_ISLAND = "rhode_island",
  SOUTH_CAROLINA = "south_carolina",
  SOUTH_DAKOTA = "south_dakota",
  TENNESSEE = "tennessee",
  TEXAS = "texas",
  UTAH = "utah",
  VERMONT = "vermont",
  VIRGINIA = "virginia",
  WASHINGTON = "washington",
  WEST_VIRGINIA = "west_virginia",
  WISCONSIN = "wisconsin",
  WYOMING = "wyoming",

  // US Federal Districts and Territories
  DISTRICT_OF_COLUMBIA = "district_of_columbia",
  AMERICAN_SAMOA = "american_samoa",
  GUAM = "guam",
  NORTHERN_MARIANA_ISLANDS = "northern_mariana_islands",
  PUERTO_RICO = "puerto_rico",
  US_VIRGIN_ISLANDS = "us_virgin_islands",

  // Canadian Provinces and Territories
  ALBERTA = "alberta",
  BRITISH_COLUMBIA = "british_columbia",
  MANITOBA = "manitoba",
  NEW_BRUNSWICK = "new_brunswick",
  NEWFOUNDLAND_AND_LABRADOR = "newfoundland_and_labrador",
  NORTHWEST_TERRITORIES = "northwest_territories",
  NOVA_SCOTIA = "nova_scotia",
  NUNAVUT = "nunavut",
  ONTARIO = "ontario",
  PRINCE_EDWARD_ISLAND = "prince_edward_island",
  QUEBEC = "quebec",
  SASKATCHEWAN = "saskatchewan",
  YUKON = "yukon",
}

export enum ETaxonomyCode {
  DENTIST = "122300000X",
  GENERAL_PRACTICE = "1223G0001X",
  DENTAL_PUBLIC_HEALTH = "1223D0001X",
  ENDODONTICS = "1223E0200X",
  ORTHODONTICS = "1223X0400X",
  PEDIATRIC_DENTISTRY = "1223P0221X",
  PERIODONTICS = "1223P0300X",
  PROSTHODONTICS = "1223P0700X",
  ORAL_MAXILLOFACIAL_PATHOLOGY = "1223P0106X",
  ORAL_MAXILLOFACIAL_RADIOLOGY = "1223X0008X",
  ORAL_MAXILLOFACIAL_SURGERY = "1223S0112X",
}

export enum EPreferredCommunicationChannel {
  EMAIL = "email",
  PHONE = "phone",
}

export const defaultPracticeStaff: Partial<TPracticeStaff> = {
  id: "",
  linked_practices: [],
  emails: [],
  phones: [],
  education: [],
  groups: [],
  first_name: "",
  last_name: "",
  picture: undefined,
  status: EStatus.ACTIVE,
  company: undefined,
  sex: undefined,
  preferred_communication_channel: undefined,
  place_of_education: "",
  licences: [],
};

export interface IPracticeStaffFilter extends DefaultPaginatedFilters {
  id__in?: string;
  linked_practices?: string[] | string;
  /**
   * this is depricated but it ised in the search path
   */
  practice_id?: string;
  company_id?: string;
  has_permission?: string;
  status?: EStatus;
  /**
   * Inlines the staff's companies (and their practices) as `companies_data`. Needed because
   * `practice/company/{id}/` answers 404 for a company outside the caller's own scope.
   */
  include_companies_data?: boolean;
}

export type PracticeStaffAPIKeyPathParams = { staffId: string };

export interface IPracticeStaffAPIKeysFilters
  extends DefaultPaginatedFilters, PracticeStaffAPIKeyPathParams {}
